import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
    Activity, 
    Mail, 
    Loader2, 
    AlertCircle,
    CheckCircle2,
    RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmailPage = () => {
    const { t } = useTranslation();
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
    const [error, setError] = useState('');

    useEffect(() => {
        const verifyEmail = async () => {
            if (!token) {
                setStatus('error');
                setError(t('errors.generic'));
                return;
            }

            try {
                await axios.get(`${API}/auth/verify-email/${token}`);
                setStatus('success');
                toast.success(t('verifyEmail.success'));
            } catch (err) {
                const message = err.response?.data?.detail || t('verifyEmail.failed');
                if (message.includes('expired')) {
                    setStatus('expired');
                } else if (message.includes('already verified')) {
                    setStatus('success');
                } else {
                    setStatus('error');
                }
                setError(message);
            }
        };

        verifyEmail();
    }, [token, t]);

    const handleResend = async () => {
        toast.info(t('verifyEmail.requestNew'));
        navigate('/login');
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-sky-50/30" data-testid="verify-email-page">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#0D2137] flex items-center justify-center shadow-xl">
                            <Activity className="w-7 h-7 text-teal-400" />
                        </div>
                        <div>
                            <span className="text-xl font-bold text-slate-800">{t('app.name')}</span>
                            <span className="block text-xs text-slate-500">{t('app.subtitle')}</span>
                        </div>
                    </Link>
                </div>

                <Card className="border-0 shadow-2xl shadow-slate-200/50">
                    <CardContent className="p-8">
                        {status === 'verifying' && (
                            <div className="text-center py-6">
                                <Loader2 className="w-12 h-12 animate-spin text-teal-500 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('verifyEmail.verifying')}</h3>
                                <p className="text-slate-500">{t('verifyEmail.pleaseWait')}</p>
                            </div>
                        )}

                        {status === 'success' && (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('verifyEmail.success')}</h3>
                                <p className="text-slate-500 mb-6">
                                    {t('verifyEmail.successDesc')}
                                </p>
                                <Link to="/login">
                                    <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-xl">
                                        {t('verifyEmail.signIn')}
                                    </Button>
                                </Link>
                            </div>
                        )}

                        {status === 'expired' && (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                                    <Mail className="w-8 h-8 text-amber-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('verifyEmail.expired')}</h3>
                                <p className="text-slate-500 mb-6">
                                    {t('verifyEmail.expiredDesc')}
                                </p>
                                <Button 
                                    onClick={handleResend}
                                    className="bg-teal-500 hover:bg-teal-600 text-white rounded-xl"
                                >
                                    <RefreshCw className="w-4 h-4 me-2" />
                                    {t('verifyEmail.requestNew')}
                                </Button>
                            </div>
                        )}

                        {status === 'error' && (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                    <AlertCircle className="w-8 h-8 text-red-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('verifyEmail.failed')}</h3>
                                <p className="text-slate-500 mb-6">{error}</p>
                                <div className="space-y-3">
                                    <Link to="/login">
                                        <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-xl w-full">
                                            {t('verifyEmail.goToSignIn')}
                                        </Button>
                                    </Link>
                                    <Link to="/register">
                                        <Button variant="outline" className="rounded-xl w-full">
                                            {t('verifyEmail.createAccount')}
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default VerifyEmailPage;
